import { useRef, useState, useCallback, useEffect } from 'react';
import {
    startRecordingSession,
    completeRecordingSession,
    updateRecordingSession,
    startQuestionSegment,
    updateQuestionSegment,
    uploadScreenRecording,
    uploadPersonRecording,
    uploadScreenshot,
} from '../Requests/QuestionnaireRecordingRequests';
import WebgazerController from '../utils/webgazerController';

function roundToOne(num) {
    return Math.round(num * 10) / 10;
}

// Debug aid: when enabled, a live red gaze dot is shown while answering the
// questionnaire so capture alignment can be watched in real time. Off by default
// (respondents must not see the dot); enable per-environment via
// REACT_APP_GAZE_DEBUG_DOT=true, or per-session with the ?gazeDebug=1 URL param.
const SHOW_LIVE_GAZE_DOT = process.env.REACT_APP_GAZE_DEBUG_DOT === 'true';

// getDisplayMedia is desktop-only; not available on iOS or most Android browsers
const canScreenRecord =
    typeof window !== 'undefined' &&
    !!navigator.mediaDevices?.getDisplayMedia;

// iOS Safari only supports mp4; Android Chrome and desktop support webm
function getSupportedMimeType() {
    if (typeof MediaRecorder === 'undefined') return '';
    const candidates = [
        'video/webm;codecs=vp8,opus',
        'video/webm;codecs=vp9,opus',
        'video/webm;codecs=vp8',
        'video/webm',
        'video/mp4;codecs=avc1',
        'video/mp4',
    ];
    for (const type of candidates) {
        if (MediaRecorder.isTypeSupported(type)) return type;
    }
    return '';
}

function mimeToExtension(mimeType) {
    return mimeType.startsWith('video/mp4') ? 'mp4' : 'webm';
}

export default function useQuestionnaireRecording() {
    const [sessionId, setSessionId] = useState(null);
    const [segmentId, setSegmentId] = useState(null);
    const [screenEnabled, setScreenEnabled] = useState(false);
    const [cameraEnabled, setCameraEnabled] = useState(false);
    const [permissionDenied, setPermissionDenied] = useState(false);
    const [cameraPermissionDenied, setCameraPermissionDenied] = useState(false);
    const [isReady, setIsReady] = useState(false);

    const streamRef = useRef(null);           // screen stream
    const cameraStreamRef = useRef(null);     // camera stream
    const mediaRecorderRef = useRef(null);    // screen recorder
    const cameraRecorderRef = useRef(null);   // camera recorder
    const chunksRef = useRef([]);
    const cameraChunksRef = useRef([]);
    const mouseEventsRef = useRef([]);
    const questionStartTimeRef = useRef(null);
    const responseUuidRef = useRef(null);
    const sessionIdRef = useRef(null);
    const segmentIdRef = useRef(null);
    const lastMouseMoveRef = useRef(0);

    // Eye-gaze (WebGazer) — mirrors the mouse-event pipeline.
    const webgazerRef = useRef(null);        // WebgazerController instance
    const gazeEventsRef = useRef([]);        // per-question {t, x%, y%} samples
    const aoisRef = useRef([]);              // per-question AOI rects (viewport %) captured at answer time
    const lastGazeSampleRef = useRef(0);     // throttle timestamp
    const gazeCaptureActiveRef = useRef(false);

    const screenEnabledRef = useRef(false);
    const cameraEnabledRef = useRef(false);
    const screenshotEnabledRef = useRef(false);
    const pendingScreenshotRef = useRef(null); // frame grabbed before the "saving" UI

    useEffect(() => { sessionIdRef.current = sessionId; }, [sessionId]);
    useEffect(() => { segmentIdRef.current = segmentId; }, [segmentId]);

    // Mouse / touch event handlers
    const handleMouseMove = useCallback((e) => {
        const now = Date.now();
        if (now - lastMouseMoveRef.current < 50) return;
        lastMouseMoveRef.current = now;
        if (questionStartTimeRef.current === null) return;
        mouseEventsRef.current.push({
            t: now - questionStartTimeRef.current,
            x: roundToOne((e.clientX / window.innerWidth) * 100),
            y: roundToOne((e.clientY / window.innerHeight) * 100),
            type: 'move',
        });
    }, []);

    const handleMouseClick = useCallback((e) => {
        if (questionStartTimeRef.current === null) return;
        mouseEventsRef.current.push({
            t: Date.now() - questionStartTimeRef.current,
            x: roundToOne((e.clientX / window.innerWidth) * 100),
            y: roundToOne((e.clientY / window.innerHeight) * 100),
            type: 'click',
        });
    }, []);

    const handleTouchMove = useCallback((e) => {
        const now = Date.now();
        if (now - lastMouseMoveRef.current < 50) return;
        lastMouseMoveRef.current = now;
        if (questionStartTimeRef.current === null) return;
        const touch = e.touches[0];
        if (!touch) return;
        mouseEventsRef.current.push({
            t: now - questionStartTimeRef.current,
            x: roundToOne((touch.clientX / window.innerWidth) * 100),
            y: roundToOne((touch.clientY / window.innerHeight) * 100),
            type: 'move',
        });
    }, []);

    const handleTouchTap = useCallback((e) => {
        if (questionStartTimeRef.current === null) return;
        const touch = e.changedTouches[0];
        if (!touch) return;
        mouseEventsRef.current.push({
            t: Date.now() - questionStartTimeRef.current,
            x: roundToOne((touch.clientX / window.innerWidth) * 100),
            y: roundToOne((touch.clientY / window.innerHeight) * 100),
            type: 'click',
        });
    }, []);

    // Buffer one gaze prediction ({x,y} in viewport px) as a per-question sample.
    // Converts to viewport percentages and throttles to ~50ms, exactly like mouse.
    const handleGazeSample = useCallback(({ x, y }) => {
        if (!gazeCaptureActiveRef.current) return;
        if (questionStartTimeRef.current === null) return;
        const now = Date.now();
        if (now - lastGazeSampleRef.current < 50) return;
        lastGazeSampleRef.current = now;
        if (!Number.isFinite(x) || !Number.isFinite(y)) return;
        gazeEventsRef.current.push({
            t: now - questionStartTimeRef.current,
            x: roundToOne((x / window.innerWidth) * 100),
            y: roundToOne((y / window.innerHeight) * 100),
        });
    }, []);

    const attachListeners = useCallback(() => {
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('click', handleMouseClick);
        window.addEventListener('touchmove', handleTouchMove, { passive: true });
        window.addEventListener('touchend', handleTouchTap);
    }, [handleMouseMove, handleMouseClick, handleTouchMove, handleTouchTap]);

    const detachListeners = useCallback(() => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('click', handleMouseClick);
        window.removeEventListener('touchmove', handleTouchMove);
        window.removeEventListener('touchend', handleTouchTap);
    }, [handleMouseMove, handleMouseClick, handleTouchMove, handleTouchTap]);

    useEffect(() => {
        return () => {
            detachListeners();
            if (webgazerRef.current) {
                webgazerRef.current.stop();
                webgazerRef.current = null;
            }
            if (streamRef.current) {
                streamRef.current.getTracks().forEach((t) => t.stop());
                streamRef.current = null;
            }
            if (cameraStreamRef.current) {
                cameraStreamRef.current.getTracks().forEach((t) => t.stop());
                cameraStreamRef.current = null;
            }
        };
    }, [detachListeners]);

    const startSession = useCallback(async (responseUuid, wantScreen, wantCamera, wantMouse = false, wantGaze = false) => {
        responseUuidRef.current = responseUuid;
        let screenGranted = false;
        let cameraGranted = false;

        // On mobile, fall back to per-question screenshots when screen recording was requested
        screenshotEnabledRef.current = wantScreen && !canScreenRecord;

        // Screen recording — desktop only
        if (wantScreen && canScreenRecord) {
            try {
                const stream = await navigator.mediaDevices.getDisplayMedia({
                    video: { displaySurface: 'browser', frameRate: 5 },
                    audio: false,
                    preferCurrentTab: true,
                });
                streamRef.current = stream;
                screenGranted = true;
            } catch (err) {
                setPermissionDenied(true);
            }
        }

        // Camera recording — works on mobile and desktop. On mobile there is a
        // single front camera and iOS won't grant two streams, so when gaze is
        // enabled WebGazer must own the camera: skip the person-recording stream
        // there (facial-emotion is unavailable alongside gaze on mobile).
        const skipPersonCamera = wantGaze && !canScreenRecord;
        if (wantCamera && !skipPersonCamera) {
            try {
                const stream = await navigator.mediaDevices.getUserMedia({
                    video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } },
                    audio: false,
                });
                cameraStreamRef.current = stream;
                cameraGranted = true;
            } catch (err) {
                setCameraPermissionDenied(true);
            }
        }

        screenEnabledRef.current = screenGranted;
        cameraEnabledRef.current = cameraGranted;
        setScreenEnabled(screenGranted);
        setCameraEnabled(cameraGranted);
        if (wantMouse) attachListeners();

        try {
            const res = await startRecordingSession(responseUuid, screenGranted, cameraGranted);
            const newSessionId = res.data.uuid || res.data.session_uuid || res.data.id;
            setSessionId(newSessionId);
            sessionIdRef.current = newSessionId;
            setIsReady(true);
            return newSessionId;
        } catch (err) {
            console.error('Failed to start recording session:', err);
            setIsReady(true);
            return null;
        }
    }, [attachListeners]);

    // Load + start WebGazer (acquires its own camera stream) so the calibration
    // screen can drive calibrate()/test() on the returned controller. Independent
    // of the person-recording camera stream — desktop browsers allow both; if the
    // second acquisition fails, gaze simply stays disabled and answering continues.
    const prepareGaze = useCallback(async () => {
        if (webgazerRef.current && webgazerRef.current.isRunning()) {
            return webgazerRef.current;
        }
        const controller = new WebgazerController();
        await controller.start(handleGazeSample);
        webgazerRef.current = controller;
        return controller;
    }, [handleGazeSample]);

    // After a passing (or skipped) calibration: hide the preview and start
    // buffering gaze samples against the per-question timeline.
    // Troubleshooting: open the questionnaire with ?gazeDebug=1 to show a live
    // red dot at the raw captured gaze position (and the webcam preview) while
    // answering, so capture alignment can be checked against actual gaze.
    const beginGazeCapture = useCallback(() => {
        if (!webgazerRef.current) return;
        // Live gaze dot during answering (SHOW_LIVE_GAZE_DOT, temporary) or when
        // ?gazeDebug=1 is set. Camera preview stays off so only the dot shows.
        const urlDebug = typeof window !== 'undefined'
            && new URLSearchParams(window.location.search).get('gazeDebug') === '1';
        webgazerRef.current.showPreview(false);
        webgazerRef.current.setDebugDot(SHOW_LIVE_GAZE_DOT || urlDebug);
        webgazerRef.current.setGazeCallback(handleGazeSample);
        gazeCaptureActiveRef.current = true;
    }, [handleGazeSample]);

    // Persist the calibration outcome onto the session (status/error/attempts).
    const persistCalibration = useCallback(async (status, errorPx, attempts) => {
        const currentResponseUuid = responseUuidRef.current;
        const currentSessionId = sessionIdRef.current;
        if (!currentResponseUuid || !currentSessionId) return;
        try {
            await updateRecordingSession(currentResponseUuid, currentSessionId, {
                gaze_calibration_status: status,
                gaze_calibration_error_px: errorPx != null ? Math.round(errorPx) : null,
                gaze_calibration_attempts: attempts,
            });
        } catch (err) {
            console.error('Failed to persist gaze calibration:', err);
        }
    }, []);

    const stopGaze = useCallback(() => {
        gazeCaptureActiveRef.current = false;
        if (webgazerRef.current) {
            webgazerRef.current.stop();
            webgazerRef.current = null;
        }
    }, []);

    const startQuestion = useCallback(async (questionUuid, order) => {
        mouseEventsRef.current = [];
        gazeEventsRef.current = [];
        aoisRef.current = [];
        questionStartTimeRef.current = Date.now();
        chunksRef.current = [];
        cameraChunksRef.current = [];

        const mimeType = getSupportedMimeType();
        const recorderOptions = mimeType ? { mimeType } : {};

        // Screen recorder
        if (screenEnabledRef.current && streamRef.current) {
            try {
                const recorder = new MediaRecorder(streamRef.current, recorderOptions);
                mediaRecorderRef.current = recorder;
                recorder.ondataavailable = (e) => {
                    if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
                };
                recorder.start();
            } catch (err) {
                console.error('Failed to start screen MediaRecorder:', err);
                mediaRecorderRef.current = null;
            }
        }

        // Camera recorder
        if (cameraEnabledRef.current && cameraStreamRef.current) {
            try {
                const recorder = new MediaRecorder(cameraStreamRef.current, recorderOptions);
                cameraRecorderRef.current = recorder;
                recorder.ondataavailable = (e) => {
                    if (e.data && e.data.size > 0) cameraChunksRef.current.push(e.data);
                };
                recorder.start();
            } catch (err) {
                console.error('Failed to start camera MediaRecorder:', err);
                cameraRecorderRef.current = null;
            }
        }

        const currentResponseUuid = responseUuidRef.current;
        const currentSessionId = sessionIdRef.current;
        if (!currentResponseUuid || !currentSessionId) return;

        let res = null;
        try {
            res = await startQuestionSegment(currentResponseUuid, currentSessionId, questionUuid, order);
        } catch (err) {
            console.warn('startQuestionSegment failed, retrying once…', err);
            try {
                await new Promise((r) => setTimeout(r, 1500));
                res = await startQuestionSegment(currentResponseUuid, currentSessionId, questionUuid, order);
            } catch (retryErr) {
                console.error('Failed to start question segment after retry:', retryErr);
            }
        }
        if (res) {
            const newSegmentId = res.data.segment_uuid || res.data.uuid || res.data.id;
            setSegmentId(newSegmentId);
            segmentIdRef.current = newSegmentId;
        }
    }, []);

    // Capture the mobile screenshot of the CURRENT question DOM. Must be called
    // while the question is still on screen — i.e. before the "saving" overlay
    // replaces it — otherwise html2canvas grabs the saving screen. endQuestion
    // then uploads this pre-captured frame instead of grabbing a fresh one.
    const captureQuestionFrame = useCallback(async () => {
        if (!screenshotEnabledRef.current) return;
        try {
            const html2canvas = (await import('html2canvas')).default;
            const canvas = await html2canvas(document.body, { scale: 0.5, useCORS: true, logging: false });
            pendingScreenshotRef.current = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.8));
        } catch (err) {
            console.error('Failed to capture question frame:', err);
        }
    }, []);

    // Store the AOI rectangles the viewer captured for the current question;
    // sent with the segment so gaze can be mapped to regions later.
    const setQuestionAois = useCallback((aois) => {
        aoisRef.current = Array.isArray(aois) ? aois : [];
    }, []);

    const endQuestion = useCallback(async (questionUuid) => {
        const endTime = Date.now();
        const startTime = questionStartTimeRef.current || endTime;
        const durationMs = endTime - startTime;

        const events = [...mouseEventsRef.current];
        mouseEventsRef.current = [];
        const gazeEvents = [...gazeEventsRef.current];
        gazeEventsRef.current = [];
        questionStartTimeRef.current = null;

        const clickCount = events.filter((e) => e.type === 'click').length;
        const moveCount = events.filter((e) => e.type === 'move').length;

        const currentResponseUuid = responseUuidRef.current;
        const currentSessionId = sessionIdRef.current;
        const currentSegmentId = segmentIdRef.current;

        // Mobile screenshot fallback — prefer the frame captured before the
        // "saving" transition (captureQuestionFrame); fall back to grabbing now.
        let screenshotBlob = null;
        if (screenshotEnabledRef.current) {
            if (pendingScreenshotRef.current) {
                screenshotBlob = pendingScreenshotRef.current;
                pendingScreenshotRef.current = null;
            } else {
                try {
                    const html2canvas = (await import('html2canvas')).default;
                    const canvas = await html2canvas(document.body, { scale: 0.5, useCORS: true, logging: false });
                    screenshotBlob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.8));
                } catch (err) {
                    console.error('Failed to capture screenshot:', err);
                }
            }
        }

        // Stop screen recorder
        let screenBlob = null;
        let screenExt = 'webm';
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
            screenBlob = await new Promise((resolve) => {
                const recorder = mediaRecorderRef.current;
                const recMime = recorder.mimeType || '';
                screenExt = mimeToExtension(recMime);
                recorder.onstop = () => {
                    const blob = new Blob(chunksRef.current, { type: recMime || 'video/webm' });
                    chunksRef.current = [];
                    resolve(blob);
                };
                recorder.stop();
            });
        }
        mediaRecorderRef.current = null;

        // Stop camera recorder
        let cameraBlob = null;
        let cameraExt = 'webm';
        if (cameraRecorderRef.current && cameraRecorderRef.current.state !== 'inactive') {
            cameraBlob = await new Promise((resolve) => {
                const recorder = cameraRecorderRef.current;
                const recMime = recorder.mimeType || '';
                cameraExt = mimeToExtension(recMime);
                recorder.onstop = () => {
                    const blob = new Blob(cameraChunksRef.current, { type: recMime || 'video/webm' });
                    cameraChunksRef.current = [];
                    resolve(blob);
                };
                recorder.stop();
            });
        }
        cameraRecorderRef.current = null;

        if (!currentResponseUuid || !currentSessionId || !currentSegmentId) return;

        // Send mouse/touch events
        try {
            await updateQuestionSegment(currentResponseUuid, currentSessionId, currentSegmentId, {
                mouse_events: events,
                gaze_events: gazeEvents,
                aois: aoisRef.current,
                duration_ms: durationMs,
                interaction_summary: {
                    total_events: events.length,
                    click_count: clickCount,
                    move_count: moveCount,
                    gaze_count: gazeEvents.length,
                    duration_ms: durationMs,
                },
            });
        } catch (err) {
            console.error('Failed to update question segment:', err);
        }

        // Upload screen recording
        if (screenBlob && screenBlob.size > 0) {
            try {
                const formData = new FormData();
                formData.append('screen_recording', screenBlob, `recording.${screenExt}`);
                await uploadScreenRecording(currentResponseUuid, currentSessionId, currentSegmentId, formData);
            } catch (err) {
                console.error('Failed to upload screen recording:', err);
            }
        }

        // Upload camera recording
        if (cameraBlob && cameraBlob.size > 0) {
            try {
                const formData = new FormData();
                formData.append('person_recording', cameraBlob, `camera.${cameraExt}`);
                await uploadPersonRecording(currentResponseUuid, currentSessionId, currentSegmentId, formData);
            } catch (err) {
                console.error('Failed to upload person recording:', err);
            }
        }

        // Upload mobile screenshot (fallback for when screen recording is unavailable)
        if (screenshotBlob && screenshotBlob.size > 0) {
            try {
                const formData = new FormData();
                formData.append('screenshot', screenshotBlob, 'screenshot.jpg');
                await uploadScreenshot(currentResponseUuid, currentSessionId, currentSegmentId, formData);
            } catch (err) {
                console.error('Failed to upload screenshot:', err);
            }
        }

        setSegmentId(null);
        segmentIdRef.current = null;
    }, []);

    const endSession = useCallback(async () => {
        detachListeners();

        gazeCaptureActiveRef.current = false;
        if (webgazerRef.current) {
            webgazerRef.current.stop();
            webgazerRef.current = null;
        }

        if (streamRef.current) {
            streamRef.current.getTracks().forEach((t) => t.stop());
            streamRef.current = null;
        }
        if (cameraStreamRef.current) {
            cameraStreamRef.current.getTracks().forEach((t) => t.stop());
            cameraStreamRef.current = null;
        }

        const currentResponseUuid = responseUuidRef.current;
        const currentSessionId = sessionIdRef.current;
        if (!currentResponseUuid || !currentSessionId) return;

        try {
            await completeRecordingSession(currentResponseUuid, currentSessionId);
        } catch (err) {
            console.error('Failed to complete recording session:', err);
        }

        setSessionId(null);
        sessionIdRef.current = null;
        setCameraEnabled(false);
        setScreenEnabled(false);
        setIsReady(false);
    }, [detachListeners]);

    return {
        sessionId,
        screenEnabled,
        cameraEnabled,
        cameraStream: cameraStreamRef,  // ref, so preview video can attach to the live stream
        permissionDenied,
        cameraPermissionDenied,
        canScreenRecord,
        isReady,
        startSession,
        startQuestion,
        setQuestionAois,
        captureQuestionFrame,
        endQuestion,
        endSession,
        // Eye-gaze (WebGazer)
        prepareGaze,
        beginGazeCapture,
        persistCalibration,
        stopGaze,
    };
}
